<template>
    <div class="content-wrapper">
        <div class="row align-items-center">
            <div class="col-md-12 col-lg-6">
                <div class="d-flex align-items-center">
                    <nav aria-label="breadcrumb">
                        <ol class="breadcrumb p-0 d-flex align-items-center mb-primary">
                            <li class="breadcrumb-item page-header d-flex align-items-center">
                                <h4 class="mb-0">{{ $t('lessons.lesson_details') }}</h4>
                            </li>
                            <template v-if="this.$can('view_lessons')">
                                <li class="ml-2">|</li>
                                <li>
                                    <a :href="urlGenerator(courseWebUrl)"
                                       class="btn btn-link text-primary pl-2">{{
                                            $t('lessons.back_to_all_courses')
                                        }}</a>
                                </li>
                            </template>
                        </ol>
                    </nav>
                </div>
            </div>
        </div>

        <div class="lesson-profile mb-primary">
            <div class="card card-with-shadow py-5 border-0" style="min-height: 220px;">
                <app-overlay-loader v-if="loader"/>

                <div v-else class="row">
                    <!-- Left Column - lesson Details -->
                    <div class="col-12 col-sm-12 col-md-6 col-lg-6 col-xl-5 border-right">
                        <div class="media px-5 lesson-header-media align-items-center mb-4">
                            <div class="media-body lesson-info-header">
                                <p class="text-muted mb-0">{{ $t('title') }}</p>
                                <p class="mb-0">{{ lesson.title }}</p>
                            </div>
                        </div>

                        <!-- Description Section -->
                        <div class="media mb-4 px-5">
                            <div class="media-body">
                                <p class="text-muted mb-0">{{ $t('lessons.video_url') }}</p>
                                <p class="mb-0 video-url" v-if="lesson.video_url">{{ lesson.video_url }}</p>
                                <p class="mb-0" v-else>{{ $t('not_added_yet') }}</p>
                            </div>
                        </div>
                    </div>

                    <!-- Right Column - Dates & Additional Info -->
                    <div class="col-12 col-sm-12 col-md-6 col-lg-6 col-xl-7">
                        <div class="lesson-details px-5 mt-4 mt-sm-5 mt-md-0 mt-lg-0 mt-xl-0">
                            <div class="row">
                                <!-- Created and Updated Date Sections -->
                                <div class="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-6">
                                    <div class="custom">
                                        <!-- Created By -->
                                        <div class="media mb-4 ">
                                            <div class="align-self-center mr-3">
                                                <app-icon name="user"/>
                                            </div>
                                            <div class="media-body">
                                                <p class="text-muted mb-0">{{ $t('created_by') }}</p>
                                                <p class="mb-0" v-if="lesson.creator?.full_name">
                                                    {{ lesson.creator.full_name }}</p>
                                                <p class="mb-0" v-else>{{ $t('not_added_yet') }}</p>
                                            </div>
                                        </div>
                                        <!-- Created Date -->
                                        <div class="media mb-4 ">
                                            <div class="align-self-center mr-3">
                                                <app-icon name="calendar"/>
                                            </div>
                                            <div class="media-body">
                                                <p class="text-muted mb-0">{{ $t('created_at') }}</p>
                                                <p class="mb-0" v-if="lesson.created_at">
                                                    {{ formatDateTimeToLocalDate(lesson.created_at) }}</p>
                                                <p class="mb-0" v-else>{{ $t('not_added_yet') }}</p>
                                            </div>
                                        </div>

                                        <!-- Updated Date -->
                                        <div class="media mb-4">
                                            <div class="align-self-center mr-3">
                                                <app-icon name="calendar"/>
                                            </div>
                                            <div class="media-body">
                                                <p class="text-muted mb-0">{{ $t('updated_at') }}</p>
                                                <p class="mb-0" v-if="lesson.updated_at">
                                                    {{ formatDateTimeToLocalDate(lesson.updated_at) }}</p>
                                                <p class="mb-0" v-else>{{ $t('not_added_yet') }}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="content-wrapper">
            <app-page-top-section :title="$t('lesson_settings')"/>
            <app-tab
                :tabs="tabs"
                icon="settings"
            />
        </div>
    </div>
</template>

<script>
import {urlGenerator} from '../../../../../common/Helper/AxiosHelper';
import {FormMixin} from "../../../../../core/mixins/form/FormMixin";
import LessonCreateEditModal from "./LessonCreateEditModal.vue";
import {formatDateTimeToLocalDate} from "../../../../../common/Helper/Support/DateTimeHelper";
import {COURSEWEB} from "../../../../Config/ApiUrl";
import {mapState} from "vuex";


export default {
    name: "LessonDetails",
    mixins: [FormMixin],
    components: {LessonCreateEditModal},
    props: {
        lessonId: {},
    },
    data() {
        return {
            lesson: {},
            courseWebUrl: COURSEWEB,
            urlGenerator,
            formatDateTimeToLocalDate,
            promptAction: '',
            modalClass: '',
            promptIcon: '',
            promptSubtitle: '',
            tabs: [
                {
                    name: this.$t('question'),
                    // title: this.$t('allowance_policy'),
                    component: "app-questions",
                    permission: this.$can('view_question'),
                    props: {
                        lesson_id: this.lessonId
                    }
                },
            ]
        };
    },
    mounted() {
        // Fetch lesson details on component mount
        this.$store.dispatch("getLessonDetails", this.lessonId);
        this.$store.dispatch('getUserInformation');
    },
    methods: {
    },
    computed: {
        ...mapState({
            lessonDetails: state => state.lesson.lesson,
            loader: state => state.loading,
        }),
    },
    watch: {
        lessonDetails: {
            handler: function (lesson) {
                this.lesson = lesson;

                // Check if lesson.title exists
                if (lesson.title) {
                    // Ensure document.title is a string and exists
                    if (typeof document.title === 'string' && document.title) {
                        let title = document.title.split('-');
                        title[0] = lesson.title;
                        document.titile = title.join(' - ');
                    } else {
                        // If document.title is undefined or not a string, initialize it properly
                        document.title = lesson.title;
                    }
                }
            },
            deep: true
        }
    }
};
</script>
<style scoped>
.video-url {
    word-break: break-all;
    white-space: normal;
    overflow-wrap: break-word;
}
</style>
